'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslations';
import { HOME_ANCHORS, ROUTES } from '@/constants/routes';
import './Header.scss';

export default function Header() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = () => setIsMenuOpen((open) => !open);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMenu]);

  const menuIconSrc = isMenuOpen ? '/close-icon.svg' : '/menu-icon.svg';

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Link
          href={ROUTES.HOME}
          className="site-header__logo"
          aria-label={t('header.homeAriaLabel')}
        >
          <Image
            src="/shift.svg"
            alt={t('header.logoAlt')}
            width={144}
            height={44}
            priority
            className="site-header__logo-image"
          />
        </Link>

        <nav
          className="site-header__nav site-header__nav--desktop"
          aria-label={t('header.navAriaLabel')}
        >
          <a href={HOME_ANCHORS.PRODUCT} className="site-header__nav-link">
            {t('header.product')}
          </a>
          <a href={HOME_ANCHORS.HOW_IT_WORKS} className="site-header__nav-link">
            {t('header.howItWorks')}
          </a>
          <a href={HOME_ANCHORS.LOOKING_FOR_WORK} className="site-header__nav-link">
            {t('header.lookingForWork')}
          </a>
        </nav>

        <button
          type="button"
          className="site-header__menu-btn"
          aria-label={isMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
          aria-expanded={isMenuOpen}
          aria-controls="site-header-mobile-menu"
          onClick={toggleMenu}
        >
          <Image
            src={menuIconSrc}
            alt=""
            width={24}
            height={24}
            className="site-header__menu-icon"
            aria-hidden
          />
        </button>
      </div>

      <button
        type="button"
        className={`site-header__overlay${isMenuOpen ? ' site-header__overlay--visible' : ''}`}
        aria-label={t('header.closeMenu')}
        onClick={closeMenu}
        tabIndex={isMenuOpen ? 0 : -1}
      />

      <div
        id="site-header-mobile-menu"
        className={`site-header__mobile-menu${isMenuOpen ? ' site-header__mobile-menu--open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        <div className="site-header__mobile-menu-top">
          <button
            type="button"
            className="site-header__mobile-close"
            aria-label={t('header.closeMenu')}
            onClick={closeMenu}
          >
            <Image
              src="/close-icon.svg"
              alt=""
              width={24}
              height={24}
              className="site-header__menu-icon"
              aria-hidden
            />
          </button>
        </div>

        <nav
          className="site-header__mobile-nav"
          aria-label={t('header.mobileNavAriaLabel')}
        >
          <a href={HOME_ANCHORS.PRODUCT} className="site-header__mobile-nav-link" onClick={closeMenu}>
            {t('header.product')}
          </a>
          <a
            href={HOME_ANCHORS.HOW_IT_WORKS}
            className="site-header__mobile-nav-link"
            onClick={closeMenu}
          >
            {t('header.howItWorks')}
          </a>
          <a
            href={HOME_ANCHORS.LOOKING_FOR_WORK}
            className="site-header__mobile-nav-link"
            onClick={closeMenu}
          >
            {t('header.lookingForWork')}
          </a>
        </nav>
      </div>
    </header>
  );
}
